"use client";

import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { Check, ChevronDown, X } from "lucide-react";
import type { ReactNode } from "react";

import { useEstimator } from "../../_context/EstimatorContext";
import { areaCaption } from "../../_logic/areas";
import type { CitySummary } from "../../_logic/city-summary";
import { DURATION, EASE_OUT, exitAfter } from "../../_logic/motion";
import type { CityEstimate } from "../../_logic/types";
import { AnimatedAmount } from "./AnimatedAmount";
import { CellSkeleton } from "./CellSkeleton";

interface CitySummaryRowProps {
  city: CityEstimate;
  summary: CitySummary;
  /** How the row reads: its place in the sort (from 1), whether it's cheapest, whether the trip has
   * dates (so amounts mean something), and whether group totals lead. */
  view: { rank: number; lowest: boolean; priced: boolean; byTotal: boolean };
  /** The city's full breakdown, shown when expanded. */
  expand: { expanded: boolean; onToggle: () => void; detail: ReactNode };
  /** Picks or unpicks the city for the side-by-side table. */
  select: { selected: boolean; onSelect: () => void };
}

/**
 * One city in the ranked list: its place, name (and what an area stands for), the per-person
 * range large on the right with the group total under it, one quiet line of what is priced, and
 * a single note when something is missing. Clicking the row (or Space or Enter on it) picks it
 * for the side-by-side table; the chevron opens its breakdown.
 * @param props - The city, its rank and summary line, and the open and pick state.
 * @returns The row.
 * @throws Never.
 */
export const CitySummaryRow = ({ city, summary, view, expand, select }: CitySummaryRowProps) => {
  const { rank, lowest, priced, byTotal } = view;
  const { expanded, onToggle, detail } = expand;
  const { selected, onSelect } = select;
  const { money, estimate, edit } = useEstimator();
  const reduceMotion = useReducedMotion();
  const caption = areaCaption(city.city);
  const loading = city.flights.loading || city.hotel.loading;
  const detailId = `city-detail-${city.city.replace(/\W+/g, "-")}`;

  return (
    <li>
      {/* The row picks the city for the side-by-side table; only the chevron opens its breakdown. */}
      <div
        className={`flex items-start transition-colors duration-150 border-l-[3px] ${selected ? "border-indigo-500 bg-indigo-50" : "border-transparent hover:bg-gray-50"}`}
      >
        <div
          role="checkbox"
          aria-checked={selected}
          // Named by its content (city, prices, Lowest), and described as the compare pick.
          aria-describedby={`${detailId}-hint`}
          tabIndex={0}
          onClick={onSelect}
          onKeyDown={(event) => {
            if (event.key !== " " && event.key !== "Enter") return;
            event.preventDefault();
            onSelect();
          }}
          className="flex min-w-0 flex-1 cursor-pointer items-start gap-3 rounded-lg py-4 pl-2 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-indigo-500 sm:pl-3"
        >
          <span aria-hidden className="mt-0.5 w-5 shrink-0 text-right text-sm font-medium tabular-nums text-gray-600">
            {rank}
          </span>
          <span
            aria-hidden
            className={`mt-1 flex size-4 shrink-0 items-center justify-center rounded border transition-colors ${selected ? "border-indigo-500 bg-indigo-500 text-white" : "border-gray-300 bg-white"}`}
          >
            {selected && <Check className="size-3" strokeWidth={2.5} />}
          </span>
          <span className="flex min-w-0 flex-1 flex-col gap-2 @[560px]:flex-row @[560px]:items-start @[560px]:gap-6">
            <span className="flex min-w-0 flex-1 flex-col gap-1">
              <span className="flex flex-wrap items-center gap-2">
                <span className="text-base font-semibold tracking-tight text-gray-900">{city.city}</span>
                {lowest && (
                  <span className="rounded-full bg-indigo-100 px-2 py-0.5 text-xs font-semibold text-indigo-700">
                    Lowest
                  </span>
                )}
              </span>
              {caption && <span className="text-xs font-medium text-gray-600">{caption}</span>}
              <span className="text-xs font-medium text-gray-600">
                {summary.facts.join(" · ")}
                {summary.warning && <span className="text-amber-700"> · {summary.warning}</span>}
              </span>
            </span>
            <span className="flex shrink-0 flex-col items-start gap-0.5 tabular-nums @[560px]:items-end @[560px]:text-right">
              {!priced ? (
                <span className="text-sm font-medium text-gray-500">Add dates to price</span>
              ) : loading ? (
                <CellSkeleton width="w-28" captions={1} />
              ) : (
                // Both views sit in the same grid cell and crossfade, so the cell keeps the wider of the two
                // and no frame is ever empty.
                <span className="grid">
                  {([false, true] as const).map((total) => (
                    <span
                      key={total ? "total" : "perPerson"}
                      aria-hidden={total !== byTotal}
                      className={`flex flex-col items-start gap-0.5 transition-opacity duration-150 [grid-area:1/1] @[560px]:items-end ${total === byTotal ? "opacity-100" : "pointer-events-none opacity-0"}`}
                    >
                      <span className="text-lg font-bold tracking-tight text-gray-900">
                        <AnimatedAmount
                          value={total ? city.total : city.perPerson}
                          format={(range) => money.formatRange(range, total)}
                        />
                        <span className="ml-1 text-xs font-medium text-gray-600">
                          {total ? `for ${estimate.headcount.toLocaleString("en-US")}` : "per person"}
                        </span>
                      </span>
                      <span className="text-xs font-medium text-gray-600">
                        <AnimatedAmount
                          value={total ? city.perPerson : city.total}
                          format={(range) => money.formatRange(range, !total)}
                        />{" "}
                        {total ? "per person" : `for ${estimate.headcount.toLocaleString("en-US")}`}
                      </span>
                    </span>
                  ))}
                </span>
              )}
            </span>
          </span>
        </div>
        {/* ui-lint-disable-next-line raw-control: a custom control (segment, chip, calendar day, stepper, icon or inline editor) styled to its row; FetchButton's sizes and 3D treatment don't fit */}
        <button
          type="button"
          onClick={() => edit({ type: "removeDestination", city: city.city }, "user")}
          aria-label={`Remove ${city.city}`}
          className="mt-3 flex size-9 shrink-0 items-center justify-center rounded-lg text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
        >
          <X aria-hidden className="size-4" />
        </button>
        {/* ui-lint-disable-next-line raw-control: a custom control (segment, chip, calendar day, stepper, icon or inline editor) styled to its row; FetchButton's sizes and 3D treatment don't fit */}
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={expanded}
          aria-controls={detailId}
          aria-label={`${expanded ? "Hide" : "Show"} ${city.city}'s breakdown`}
          className="m-2 mt-3 flex size-9 shrink-0 items-center justify-center rounded-lg text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
        >
          <ChevronDown
            aria-hidden
            className={`size-4 transition-transform duration-150 ${expanded ? "rotate-180" : ""}`}
          />
        </button>
      </div>
      <span id={`${detailId}-hint`} className="sr-only">
        Select to compare side by side
      </span>
      <AnimatePresence initial={false}>
        {expanded && (
          <motion.div
            key="detail"
            id={detailId}
            initial={reduceMotion ? { opacity: 0 } : { opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={
              reduceMotion
                ? { opacity: 0, transition: exitAfter(DURATION.fast) }
                : { opacity: 0, height: 0, transition: exitAfter(DURATION.base) }
            }
            transition={{ duration: reduceMotion ? DURATION.fast : DURATION.base, ease: EASE_OUT }}
            className="overflow-hidden"
          >
            <div className="pb-4 @[560px]:pl-10">{detail}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </li>
  );
};
